import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Compass, Map, MessagesSquare, UserRound } from "lucide-react";
import { api } from "@/lib/api";
import { ThisWeek } from "@/components/ThisWeek";
import { useAuth } from "@/context/AuthContext";

const Tile = ({ to, icon: Icon, label, value, hint, testid, span = "" }) => (
  <Link
    to={to}
    data-testid={testid}
    className={`panel p-8 group hover:bg-white/[0.05] transition-colors duration-200 flex flex-col ${span}`}
  >
    <div className="flex items-center gap-3">
      <Icon className="h-4 w-4 text-primary" strokeWidth={1.6} />
      <span className="overline">{label}</span>
      <ArrowUpRight className="h-4 w-4 ml-auto text-muted-foreground group-hover:text-white group-hover:-translate-y-0.5 transition-[color,transform] duration-200" />
    </div>
    <p className="mt-8 font-display text-3xl tracking-tight">{value}</p>
    <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{hint}</p>
  </Link>
);

export default function Dashboard() {
  const { user } = useAuth();
  const [state, setState] = useState({ msgs: 0, profile: null, paths: 0, roadmaps: [] });

  useEffect(() => {
    (async () => {
      const [m, p, pa, r] = await Promise.all([
        api.get("/interview/messages").catch(() => ({ messages: [] })),
        api.get("/profile").catch(() => null),
        api.get("/paths").catch(() => ({ paths: [] })),
        api.get("/roadmaps").catch(() => ({ roadmaps: [] })),
      ]);
      setState({
        msgs: (m.messages || []).filter((x) => x.role === "user").length,
        profile: p,
        paths: (pa?.paths || []).length,
        roadmaps: r.roadmaps || [],
      });
    })();
  }, []);

  const rm = state.roadmaps[0];
  const totalSteps = rm ? rm.phases.reduce((a, ph) => a + (ph.checklist?.length || 0), 0) : 0;
  const doneSteps = rm ? (rm.completed || []).length : 0;

  return (
    <div className="space-y-8" data-testid="dashboard">
      <div className="rise">
        <p className="overline">Command centre</p>
        <h1 className="mt-4 font-display text-4xl sm:text-5xl tracking-tight leading-none">
          {state.msgs === 0 ? "Let's start with a conversation" : `Welcome back, ${user?.name?.split(" ")[0]}`}
        </h1>
        <p className="mt-5 text-base text-muted-foreground max-w-2xl leading-relaxed">
          {state.msgs === 0
            ? "Loop knows nothing about you yet. Ten minutes of honest conversation is all it takes to start seeing real options — a career, a course, a hobby, a project, a place to live, a way to make money."
            : "Everything Loop has learned lives here. Nothing moves forward unless you say so."}
        </p>
      </div>

      <ThisWeek />

      <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-4">
        <Tile
          to="/interview"
          icon={MessagesSquare}
          label="Step 01 — Interview"
          value={state.msgs === 0 ? "Not started" : `${state.msgs} answers`}
          hint={state.msgs === 0 ? "Loop asks one question at a time." : "Continue the conversation anytime."}
          testid="tile-interview"
        />
        <Tile
          to="/profile"
          icon={UserRound}
          label="Step 02 — Profile"
          value={state.profile ? "Ready to review" : "Locked"}
          hint={state.profile ? "Edit anything Loop got wrong." : "Finish some of the interview first."}
          testid="tile-profile"
        />
        <Tile
          to="/paths"
          icon={Compass}
          label="Step 03 — Possibilities"
          value={state.paths ? `${state.paths} options` : "Waiting"}
          hint="Careers, courses, hobbies, places, projects — plus wildcards."
          testid="tile-paths"
        />
        <Tile
          to="/roadmap"
          icon={Map}
          label="Step 04 — Roadmap"
          value={rm ? `${doneSteps}/${totalSteps} steps` : "Waiting"}
          hint={rm ? rm.path_title : "Pick a path to generate your roadmap."}
          testid="tile-roadmap"
        />
      </div>

      {state.profile && (
        <div className="panel p-8 rise" data-testid="dashboard-profile-summary">
          <span className="overline">What loop understands about you</span>
          <p className="mt-5 font-display text-lg md:text-lg leading-relaxed max-w-4xl">
            {state.profile.summary}
          </p>
          <div className="mt-7 flex flex-wrap gap-2">
            {[...(state.profile.natural_strengths || []), ...(state.profile.interests || [])]
              .slice(0, 10)
              .map((s) => (
                <span key={s} className="text-xs px-3 py-1.5 rounded-full border border-white/10 text-muted-foreground">
                  {s}
                </span>
              ))}
          </div>
        </div>
      )}
    </div>
  );
}
